import {
  HealthSystemAPIControllersAnimalsRequestsCreateAnimalExternalIdentifierRequest,
  HealthSystemDomainAnimalsModelsAnimalExternalIdentifierViewModel
} from '../api/HealthSystemApi';

/**
 * Local mirror of the backend `AnimalExternalIdentifierType` values (AB#3739).
 *
 * The generated client (src/api/HealthSystemApi.ts) has not been regenerated against the updated
 * backend yet, so it still lacks the `type` field on both the view model and the create request.
 * Everything in this file is a temporary shim and should be deleted once `bun run swagger` is run
 * against a backend that exposes `type`.
 */
export const ANIMAL_EXTERNAL_IDENTIFIER_TYPES = [
  'PlasticNumber',
  'Tattoo',
  'BirthId',
  'HerdbookId',
  'SlaughterId',
  'Rfid',
  'ExternalSystemId',
  'Other'
] as const;

export type AnimalExternalIdentifierType = (typeof ANIMAL_EXTERNAL_IDENTIFIER_TYPES)[number];

/** The backend defaults an omitted type to `Other`; the add dialog mirrors that default. */
export const DEFAULT_ANIMAL_EXTERNAL_IDENTIFIER_TYPE: AnimalExternalIdentifierType = 'Other';

export const AnimalExternalIdentifierTypeLabelKeys: Record<AnimalExternalIdentifierType, string> = {
  PlasticNumber: 'f-external-id-type-plastic-number',
  Tattoo: 'f-external-id-type-tattoo',
  BirthId: 'f-external-id-type-birth-id',
  HerdbookId: 'f-external-id-type-herdbook-id',
  SlaughterId: 'f-external-id-type-slaughter-id',
  Rfid: 'f-external-id-type-rfid',
  ExternalSystemId: 'f-external-id-type-external-system-id',
  Other: 'f-external-id-type-other'
};

export const isAnimalExternalIdentifierType = (value: unknown): value is AnimalExternalIdentifierType =>
  typeof value === 'string' && (ANIMAL_EXTERNAL_IDENTIFIER_TYPES as readonly string[]).includes(value);

/**
 * View model returned by GET /animals/{id}/external-identifiers.
 *
 * This used to add an optional `type` while the backend was still rolling the field out. The generated
 * client now declares it as a required string, so the alias is all that is left - narrowing it further
 * would collapse to `string` anyway, since the API can send a type this frontend does not know yet.
 * Use `isAnimalExternalIdentifierType` to check for a known one.
 */
export type AnimalExternalIdentifierViewModel = HealthSystemDomainAnimalsModelsAnimalExternalIdentifierViewModel;

/** Body of POST /animals/{id}/external-identifiers, extended with the optional `type`. */
export interface CreateAnimalExternalIdentifierRequest
  extends HealthSystemAPIControllersAnimalsRequestsCreateAnimalExternalIdentifierRequest {
  type?: AnimalExternalIdentifierType;
}

/**
 * Picks the plastic ear tag number out of an animal's external identifiers.
 * Returns an empty string when the animal has none - callers must never fall back to another number.
 */
export const findPlasticNumber = (identifiers: AnimalExternalIdentifierViewModel[]): string =>
  identifiers.find((it) => it.type === 'PlasticNumber')?.value ?? '';
